import { Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { LegalFooter } from '../components/layout';
import { Btn, Logo } from '../components/ui';
import { TextField } from '../components/forms';
import { TurnstileWidget } from '../components/forms/TurnstileWidget';
import { ApiError } from '../api/errors';
import { useAuthStore } from '../stores/authStore';
import { c } from '../styles/theme';
import { paths } from './paths';

const ICON_SIZE = 14;

export default function VerifyEmailPending() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const resendVerificationEmail = useAuthStore((s) => s.resendVerificationEmail);
  const logout = useAuthStore((s) => s.logout);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [email, setEmail] = useState<string>(location.state?.email ?? user?.email ?? '');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const justSent = location.state?.emailJustSent === true;

  useEffect(() => {
    if (user?.emailVerifiedAt) {
      navigate(paths.dashboard, { replace: true });
    }
  }, [navigate, user?.emailVerifiedAt]);

  async function handleResend() {
    if (!email.trim()) return;
    setPending(true);
    setError('');
    setNotice('');
    try {
      await resendVerificationEmail(email, turnstileToken);
      setNotice('If this account needs verification, we sent a fresh verification email.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not resend verification email');
    } finally {
      setPending(false);
      setTurnstileToken('');
      setTurnstileResetKey((value) => value + 1);
    }
  }

  function handleBackToLogin() {
    logout();
    navigate(paths.login, { replace: true });
  }

  return (
    <div
      style={{
        fontFamily: 'Inter, sans-serif',
        background: c.bg,
        color: c.text1,
        width: '100%',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <Logo size={30} />
        </div>

        <div
          style={{
            background: c.surface1,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            padding: '24px 22px',
            textAlign: 'center',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              margin: '0 auto 14px',
              borderRadius: '999px',
              background: c.surface2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Mail size={ICON_SIZE} color={c.accent} />
          </div>

          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 18,
              margin: '0 0 6px',
              color: c.text1,
            }}
          >
            Check your email
          </h1>
          <p style={{ fontSize: 12, color: c.text3, margin: '0 0 14px', lineHeight: 1.6 }}>
            {justSent
              ? `We sent a verification email${email ? ` to ${email}` : ''}. Open that link to continue.`
              : 'Check your inbox or send another verification email.'}
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: '0 0 16px', lineHeight: 1.6 }}>
            Open the link and choose your password to verify your email. You can also complete
            Forgot password to confirm ownership and access your account.
          </p>

          {error && (
            <p role="alert" style={{ fontSize: 12, color: c.danger, margin: '0 0 12px' }}>
              {error}
            </p>
          )}
          {!error && notice && (
            <p role="status" style={{ fontSize: 12, color: c.text2, margin: '0 0 12px' }}>
              {notice}
            </p>
          )}

          <TextField label="Email address" type="email" value={email} onChange={setEmail} />
          <TurnstileWidget
            action="verification_resend"
            resetKey={turnstileResetKey}
            onTokenChange={setTurnstileToken}
          />
          <Btn
            full
            type="button"
            onClick={() => void handleResend()}
            disabled={pending || !email.trim() || !turnstileToken}
            style={{ marginBottom: 10 }}
          >
            {pending ? 'Sending…' : 'Resend verification email'}
          </Btn>

          <Btn full type="button" onClick={() => navigate(paths.resetPassword)} style={{ marginBottom: 10 }}>
            Forgot password
          </Btn>

          <Btn full type="button" onClick={handleBackToLogin} style={{ marginBottom: 10 }}>
            Back to login
          </Btn>
        </div>

        <LegalFooter marginTop={20} centered />
      </div>
    </div>
  );
}
